import React from 'react';
import { Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import { useAuthBootstrap } from '@/hooks/useAuthBootstrap';
import type { RootState } from '@/store';
import { ACCESS_TOKEN_KEY } from '@/services/api';

/**
 * AuthGuard — opaque token variant.
 *
 * Strategy: check localStorage for `padm_access_token`.
 * If not present → redirect to /login.
 * If present but Redux user empty → show spinner while bootstrap hydrates.
 * Once user is in Redux → render children.
 *
 * Actual token validity is handled by the api.ts 401 interceptor (auto-refresh).
 * No JWT decoding here.
 */
export const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { bootstrapping } = useAuthBootstrap();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (bootstrapping) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;

